import React from 'react';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useCheckoutTimer, getTimerColorClass, getTimerUrgency, TimeRemaining } from '../hooks/useCheckoutTimer';

export interface CheckoutTimerProps {
  expiresAt?: string | null;
  isCheckedOut?: boolean;
  onExpired?: () => void;
  className?: string;
  showIcon?: boolean;
  compact?: boolean;
}

/**
 * CheckoutTimer Component
 * Displays remaining checkout time with real-time updates
 */
export function CheckoutTimer({ 
  expiresAt, 
  isCheckedOut = false, 
  onExpired,
  className = '',
  showIcon = true,
  compact = false
}: CheckoutTimerProps) {
  
  const timeRemaining = useCheckoutTimer({ expiresAt, isCheckedOut, onExpired });

  // Don't render if not checked out or no expiration time
  if (!isCheckedOut || !expiresAt) {
    return null;
  }

  const urgency = getTimerUrgency(timeRemaining);
  const colorClass = getTimerColorClass(timeRemaining);

  // Get appropriate icon based on urgency
  const getIcon = () => {
    if (urgency === 'expired' || urgency === 'critical') {
      return <ExclamationTriangleIcon className="w-3 h-3" />;
    }
    return <ClockIcon className="w-3 h-3" />;
  };

  // Get background color based on urgency
  const getBgClass = () => {
    switch (urgency) {
      case 'expired':
        return 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      case 'critical':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      default:
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    }
  };

  // Compact version for inline display
  if (compact) {
    return (
      <span className={`inline-flex items-center space-x-1 ${colorClass} ${className}`}>
        {showIcon && getIcon()}
        <span className="text-xs font-medium">
          {timeRemaining.formattedTime}
        </span>
      </span>
    );
  }

  // Full version with background and border
  return (
    <div className={`
      inline-flex items-center space-x-1 px-2 py-1 rounded-md border text-xs font-medium
      ${getBgClass()} ${colorClass} ${className}
    `}>
      {showIcon && getIcon()}
      <span>
        {timeRemaining.isExpired ? 'Lock auto-released' : `${timeRemaining.formattedTime} until auto-release`}
      </span>
    </div>
  );
}

/**
 * CheckoutTimerBadge - Minimal badge version
 */
export function CheckoutTimerBadge({ 
  expiresAt, 
  isCheckedOut = false, 
  className = '' 
}: Omit<CheckoutTimerProps, 'onExpired' | 'showIcon' | 'compact'>) {
  
  return (
    <CheckoutTimer
      expiresAt={expiresAt}
      isCheckedOut={isCheckedOut}
      className={className}
      showIcon={false}
      compact={true}
    />
  );
}

/**
 * CheckoutTimerTooltip - For use in tooltips
 */
export function CheckoutTimerTooltip({ 
  expiresAt, 
  isCheckedOut = false 
}: Pick<CheckoutTimerProps, 'expiresAt' | 'isCheckedOut'>) {
  
  const timeRemaining = useCheckoutTimer({ expiresAt, isCheckedOut });

  if (!isCheckedOut || !expiresAt) {
    return null;
  }

  const expiration = new Date(expiresAt);
  const formattedExpiration = expiration.toLocaleString();

  return (
    <div className="text-sm">
      <div className="font-medium">
        {timeRemaining.isExpired ? 'Checkout lock auto-release reached' : `${timeRemaining.formattedTime} until auto-release`}
      </div>
      <div className="text-gray-500 dark:text-gray-400 text-xs">
        Auto-release lock: {formattedExpiration}
      </div>
    </div>
  );
}
